import type {
  CommerceIntentDto,
  CommerceAppointmentTimeslotSelectionDto,
  CommerceRequestedAction,
  CreateCommerceQuoteDtoOffering,
} from "@zipper/customer-commerce-client";
import { assign, sendTo, setup, type ActorRefFrom } from "xstate";
import {
  checkoutMachine,
  type CheckoutEvent,
  type CheckoutMachineOutput,
} from "../checkout/checkout.machine";
import type {
  CheckoutFailure,
  CheckoutGateway,
  CheckoutRuntimeIdentity,
} from "../checkout/actors";

export interface CommerceSurfaceLaunch {
  offering: CreateCommerceQuoteDtoOffering;
  requestedAction: CommerceRequestedAction;
  requestedTargetKey: string | null;
  quantity: number;
  customAmountMinor: number | null;
  timeslots: readonly CommerceAppointmentTimeslotSelectionDto[];
}

export interface CommerceSurfaceContext {
  runtime: CheckoutRuntimeIdentity;
  gateway: CheckoutGateway;
  launch: CommerceSurfaceLaunch | null;
  recoverableIntentId: string | null;
  completedIntent: CommerceIntentDto | null;
  cancelledIntent: CommerceIntentDto | null;
  failure: CheckoutFailure | null;
}

export interface CommerceSurfaceInput {
  runtime: CheckoutRuntimeIdentity;
  gateway: CheckoutGateway;
}

export type CommerceSurfaceEvent =
  | { type: "RUNTIME.RECONCILED"; runtime: CheckoutRuntimeIdentity }
  | {
      type: "OFFERING.OPENED";
      offering: CreateCommerceQuoteDtoOffering;
      requestedAction: CommerceRequestedAction;
      requestedTargetKey?: string | null;
      quantity?: number;
      customAmountMinor?: number | null;
      timeslots?: readonly CommerceAppointmentTimeslotSelectionDto[];
    }
  | { type: "CHECKOUT.EVENT"; event: CheckoutEvent }
  | {
      type: "CHECKOUT.DETACHED";
      intentId: string;
      failure?: CheckoutFailure;
    }
  | { type: "CHECKOUT.FAILED"; failure: CheckoutFailure }
  | { type: "RECOVERY.RESUME" }
  | { type: "SURFACE.RESET" };

function surfaceLaunch(
  event: Extract<CommerceSurfaceEvent, { type: "OFFERING.OPENED" }>,
): CommerceSurfaceLaunch {
  return {
    offering: event.offering,
    requestedAction: event.requestedAction,
    requestedTargetKey: event.requestedTargetKey ?? null,
    quantity: Math.max(1, event.quantity ?? 1),
    customAmountMinor: event.customAmountMinor ?? null,
    timeslots: event.timeslots ?? [],
  };
}

export const surfaceMachine = setup({
  types: {
    context: {} as CommerceSurfaceContext,
    events: {} as CommerceSurfaceEvent,
    input: {} as CommerceSurfaceInput,
  },
  actors: {
    checkout: checkoutMachine,
  },
  guards: {
    checkoutSucceeded: (_, params: { output: CheckoutMachineOutput }) =>
      params.output.status === "succeeded",
    canResumeRecovery: ({ context }) =>
      context.launch !== null && context.recoverableIntentId !== null,
  },
  actions: {
    rememberRuntime: assign({
      runtime: (_, params: { runtime: CheckoutRuntimeIdentity }) =>
        params.runtime,
    }),
    rememberLaunch: assign({
      launch: (
        _,
        params: {
          event: Extract<CommerceSurfaceEvent, { type: "OFFERING.OPENED" }>;
        },
      ) => surfaceLaunch(params.event),
      completedIntent: null,
      cancelledIntent: null,
      recoverableIntentId: null,
      failure: null,
    }),
    rememberCompletion: assign({
      completedIntent: (_, params: { output: CheckoutMachineOutput }) =>
        params.output.status === "succeeded" ? params.output.intent : null,
      recoverableIntentId: null,
      cancelledIntent: null,
      failure: null,
    }),
    rememberCancellation: assign({
      cancelledIntent: (_, params: { output: CheckoutMachineOutput }) =>
        params.output.status === "cancelled" ? params.output.intent : null,
      recoverableIntentId: null,
      completedIntent: null,
      failure: null,
    }),
    rememberRecovery: assign({
      recoverableIntentId: (
        _,
        params: {
          intentId: string;
          failure: CheckoutFailure | undefined;
        },
      ) => params.intentId,
      failure: (
        _,
        params: {
          intentId: string;
          failure: CheckoutFailure | undefined;
        },
      ) => params.failure ?? null,
    }),
    rememberFailure: assign({
      failure: (_, params: { failure: CheckoutFailure }) => params.failure,
    }),
    resetSurface: assign({
      launch: null,
      recoverableIntentId: null,
      completedIntent: null,
      cancelledIntent: null,
      failure: null,
    }),
    forwardRuntime: sendTo(
      "checkout",
      (_, params: { runtime: CheckoutRuntimeIdentity }) => ({
        type: "RUNTIME.RECONCILED",
        runtime: params.runtime,
      }),
    ),
    forwardCheckoutEvent: sendTo(
      "checkout",
      (_, params: { event: CheckoutEvent }) => params.event,
    ),
  },
}).createMachine({
  id: "commerceSurface",
  context: ({ input }) => ({
    runtime: input.runtime,
    gateway: input.gateway,
    launch: null,
    recoverableIntentId: null,
    completedIntent: null,
    cancelledIntent: null,
    failure: null,
  }),
  initial: "browsing",
  on: {
    "OFFERING.OPENED": {
      target: ".checkout",
      reenter: true,
      actions: {
        type: "rememberLaunch",
        params: ({ event }) => ({ event }),
      },
    },
    "SURFACE.RESET": {
      target: ".browsing",
      actions: "resetSurface",
    },
  },
  states: {
    browsing: {
      on: {
        "RUNTIME.RECONCILED": {
          actions: {
            type: "rememberRuntime",
            params: ({ event }) => ({ runtime: event.runtime }),
          },
        },
      },
    },
    checkout: {
      invoke: {
        id: "checkout",
        src: "checkout",
        input: ({ context }) => ({
          runtime: context.runtime,
          gateway: context.gateway,
          offering: context.launch!.offering,
          requestedAction: context.launch!.requestedAction,
          requestedTargetKey: context.launch!.requestedTargetKey,
          quantity: context.launch!.quantity,
          customAmountMinor: context.launch!.customAmountMinor,
          timeslots: context.launch!.timeslots,
          recoveryIntentId: context.recoverableIntentId,
        }),
        onDone: [
          {
            guard: {
              type: "checkoutSucceeded",
              params: ({ event }) => ({ output: event.output }),
            },
            target: "succeeded",
            actions: {
              type: "rememberCompletion",
              params: ({ event }) => ({ output: event.output }),
            },
          },
          {
            target: "cancelled",
            actions: {
              type: "rememberCancellation",
              params: ({ event }) => ({ output: event.output }),
            },
          },
        ],
      },
      on: {
        "RUNTIME.RECONCILED": {
          actions: [
            {
              type: "rememberRuntime",
              params: ({ event }) => ({ runtime: event.runtime }),
            },
            {
              type: "forwardRuntime",
              params: ({ event }) => ({ runtime: event.runtime }),
            },
          ],
        },
        "CHECKOUT.EVENT": {
          actions: {
            type: "forwardCheckoutEvent",
            params: ({ event }) => ({ event: event.event }),
          },
        },
        "CHECKOUT.DETACHED": {
          target: "recoverable",
          actions: {
            type: "rememberRecovery",
            params: ({ event }) => ({
              intentId: event.intentId,
              failure: event.failure,
            }),
          },
        },
        "CHECKOUT.FAILED": {
          target: "failed",
          actions: {
            type: "rememberFailure",
            params: ({ event }) => ({ failure: event.failure }),
          },
        },
      },
    },
    recoverable: {
      on: {
        "RUNTIME.RECONCILED": {
          actions: {
            type: "rememberRuntime",
            params: ({ event }) => ({ runtime: event.runtime }),
          },
        },
        "RECOVERY.RESUME": {
          guard: "canResumeRecovery",
          target: "checkout",
        },
      },
    },
    succeeded: {
      on: {
        "RUNTIME.RECONCILED": {
          actions: {
            type: "rememberRuntime",
            params: ({ event }) => ({ runtime: event.runtime }),
          },
        },
      },
    },
    cancelled: {
      on: {
        "RUNTIME.RECONCILED": {
          actions: {
            type: "rememberRuntime",
            params: ({ event }) => ({ runtime: event.runtime }),
          },
        },
      },
    },
    failed: {
      on: {
        "RUNTIME.RECONCILED": {
          actions: {
            type: "rememberRuntime",
            params: ({ event }) => ({ runtime: event.runtime }),
          },
        },
      },
    },
  },
});

export type CommerceSurfaceActorRef = ActorRefFrom<typeof surfaceMachine>;
